import { useState } from "react";
import { ChevronDown, ChevronUp, Phone, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import EnhancedSEO from "@/components/SEO/EnhancedSEO";

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const FAQ = () => {
  const [openItems, setOpenItems] = useState<number[]>([]);

  const toggleItem = (index: number) => {
    setOpenItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const faqData: FAQItem[] = [
    // Pricing & ROI Questions
    {
      category: "Pricing & ROI",
      question: "How much do your services cost?",
      answer: "Our pricing varies based on your specific needs and goals. We offer packages starting from £1,500/month for our Growth Start package, up to £8,500/month for Enterprise Elite. Each package includes different services and levels of support. Book a strategy call to get a custom quote."
    },
    {
      category: "Pricing & ROI",
      question: "What ROI can I expect from your marketing services?",
      answer: "Our clients typically see an average of 150% revenue increase within 12 months. However, ROI varies based on industry, current digital presence, and investment level. We provide detailed ROI projections during our strategy calls."
    },
    {
      category: "Pricing & ROI",
      question: "Do you offer payment plans or monthly billing?",
      answer: "Yes, all our packages are billed monthly with no long-term contracts. You can cancel anytime with 30 days notice. We also offer quarterly payment discounts for committed clients."
    },
    {
      category: "Pricing & ROI",
      question: "How do I know if your work is actually getting me clients?",
      answer: "Transparency is key. We install tracking systems that show exactly where leads come from (ads, SEO, Google Maps, etc.). You'll see real numbers, not vague promises."
    },
    {
      category: "Pricing & ROI",
      question: "What's included in the setup fees?",
      answer: "Setup fees cover initial strategy development, account setups, tracking implementation, website audits, and campaign creation. This is a one-time investment that sets the foundation for your ongoing success."
    },
    {
      category: "Pricing & ROI",
      question: "Is there a minimum contract length?",
      answer: "No. We believe in earning your business month after month. Our systems are designed to produce measurable growth, so you'll stay with us because it works—not because you're trapped."
    },
    {
      category: "Pricing & ROI",
      question: "What if I'm not seeing results?",
      answer: "We provide monthly reports showing exactly what's working and what isn't. If results aren't meeting expectations, we adjust strategies immediately. Our success is tied to your success."
    },
    {
      category: "Pricing & ROI",
      question: "Do you guarantee specific results?",
      answer: "While we can't guarantee specific numbers due to market variables, we do guarantee our commitment to optimization and transparent reporting. Most clients see significant improvements within 60-90 days."
    },

    // Process Questions
    {
      category: "Process & Timeline",
      question: "How long does SEO take to show results?",
      answer: "SEO is a long game. That's why we combine fast wins with ads while building your SEO foundation. You'll get leads this week from ads and sustainable growth from SEO over 6-12 months."
    },
    {
      category: "Process & Timeline",
      question: "When will I see results from paid advertising?",
      answer: "Paid ads can generate leads within 24-48 hours of launch. However, we typically see optimal performance after 2-4 weeks of testing and optimization."
    },
    {
      category: "Process & Timeline",
      question: "What's your onboarding process like?",
      answer: "Week 1: Strategy call and audit completion. Week 2: Account setups and tracking implementation. Week 3: Campaign launches and initial optimization. You'll start seeing activity immediately."
    },
    {
      category: "Process & Timeline",
      question: "How often do we communicate?",
      answer: "You'll receive weekly updates via email, monthly detailed reports, and have direct access to your account manager. We also schedule monthly strategy calls to review performance and plan ahead."
    },
    {
      category: "Process & Timeline",
      question: "What happens during the strategy call?",
      answer: "We audit your digital presence and recommend the fastest path to ROI, whether that's quick wins with ads, building SEO for long-term growth, or optimizing your website for conversions."
    },
    {
      category: "Process & Timeline",
      question: "How quickly can you start working on my account?",
      answer: "We can typically begin within 5-7 business days of signing. Emergency setups can be arranged for urgent situations within 24-48 hours."
    },
    {
      category: "Process & Timeline",
      question: "Do you work with new businesses or only established ones?",
      answer: "We work with both, but our strategies differ. New businesses often need quick wins through ads and conversion optimization, while established businesses might benefit more from SEO and advanced funnels."
    },

    // Technical Setup FAQs
    {
      category: "Technical Setup",
      question: "Do I need to install tracking pixels myself?",
      answer: "No, we handle all technical setup including Facebook Pixel, Google Analytics, Google Tag Manager, and conversion tracking. You don't need any technical knowledge."
    },
    {
      category: "Technical Setup",
      question: "What reporting dashboards will I have access to?",
      answer: "You'll get access to custom dashboards showing lead sources, conversion rates, ROI, keyword rankings, and more. Everything is organized in easy-to-understand reports."
    },
    {
      category: "Technical Setup",
      question: "Will you need access to my website?",
      answer: "Yes, we'll need admin access to implement tracking codes, optimize for conversions, and make SEO improvements. We follow strict security protocols and only make approved changes."
    },
    {
      category: "Technical Setup",
      question: "What if my website isn't optimized for mobile?",
      answer: "Mobile optimization is crucial for modern marketing. We'll audit your site and recommend necessary improvements or redesign options to ensure optimal performance across all devices."
    },
    {
      category: "Technical Setup",
      question: "Do you integrate with CRM systems?",
      answer: "Yes, we integrate with most major CRM systems including HubSpot, Salesforce, Pipedrive, and more. This ensures seamless lead management and accurate ROI tracking."
    },
    {
      category: "Technical Setup",
      question: "How do you track phone calls from marketing?",
      answer: "We use call tracking numbers that dynamically replace your website number based on traffic source. This lets us track which campaigns generate phone leads and their conversion rates."
    },
    {
      category: "Technical Setup",
      question: "What happens to my existing analytics and tracking?",
      answer: "We'll audit your current setup and enhance it rather than replace it. Your historical data remains intact while we add more sophisticated tracking for better insights."
    },

    // Industry & Niching Questions
    {
      category: "Industry & Targeting",
      question: "Do you only work with landscapers?",
      answer: "No, while we have great success with landscapers, we work with various industries including construction, professional services, healthcare, e-commerce, and more. Our strategies adapt to your specific market."
    },
    {
      category: "Industry & Targeting",
      question: "What other industries do you help?",
      answer: "We specialize in service-based businesses including legal firms, healthcare practices, home improvement companies, professional consultants, real estate agents, and local retailers."
    },
    {
      category: "Industry & Targeting",
      question: "Do you work with businesses outside the UK?",
      answer: "Primarily we focus on UK-based businesses, but we do work with international clients on a case-by-case basis, especially for digital services and e-commerce businesses."
    },
    {
      category: "Industry & Targeting",
      question: "What if my industry is very competitive?",
      answer: "Competitive industries often have the highest ROI potential. We use advanced targeting, competitive analysis, and proven strategies to help you stand out and capture market share."
    },
    {
      category: "Industry & Targeting",
      question: "Do you work with B2B or B2C businesses?",
      answer: "Both! Our strategies adapt to your target audience. B2B campaigns focus on LinkedIn, email marketing, and longer sales cycles, while B2C emphasizes social media, search ads, and immediate conversions."
    },
    {
      category: "Industry & Targeting",
      question: "Can you help with national vs local targeting?",
      answer: "Absolutely. Local businesses benefit from geo-targeted ads and local SEO, while national companies need broader strategies. We customize targeting based on your service area and goals."
    },

    // Common Objections & Concerns
    {
      category: "Common Concerns",
      question: "Meta ads are too expensive now; can they still work?",
      answer: "Yes—when done correctly. Costs rise when ads target broad or uninterested audiences. At VGP, we use data-backed targeting, retargeting, and split testing to drive your cost-per-lead down while increasing ROI."
    },
    {
      category: "Common Concerns",
      question: "We're already too busy... wouldn't more leads just overload us?",
      answer: "That's the best problem to have—and we solve it strategically. At VGP, we don't just flood you with leads. We help you position your services at a higher ticket price, so you can: Take on fewer jobs, Work with better clients, Earn more revenue without stretching your team thin."
    },
    {
      category: "Common Concerns",
      question: "I've tried Meta ads before, and they just wasted money without results. How will this be different?",
      answer: "Most agencies boost reach without focusing on conversions. At VGP, we build targeted campaigns around your ideal clients, using precision audience targeting and compelling creative that speaks directly to them. We track every click and call so your spend turns into booked jobs, not wasted ad dollars."
    },
    {
      category: "Common Concerns",
      question: "SEO takes too long. I need leads now.",
      answer: "True—SEO is a long game. That's why we combine fast wins with ads while building your SEO foundation. You'll get leads this week from ads and sustainable growth from SEO."
    },
    {
      category: "Common Concerns",
      question: "I don't know where to start—ads, SEO, or a new website?",
      answer: "That's exactly why we offer a free strategy call. We'll audit your digital presence and recommend the fastest path to ROI, whether that's quick wins with ads, building SEO for long-term growth, or optimizing your website for conversions."
    },
    {
      category: "Common Concerns",
      question: "I'm worried about wasting time learning new tools.",
      answer: "You don't have to. We handle the tech, automations, and integrations. You just pick up the phone and close booked calls."
    },
    {
      category: "Common Concerns",
      question: "We don't have the staff to handle more jobs.",
      answer: "You don't need to. We help you raise your ticket price and focus on bigger, higher-value jobs—so you work less, earn more, and stop chasing small clients."
    },
    {
      category: "Common Concerns",
      question: "Our website looks good but doesn't generate leads.",
      answer: "Pretty websites don't pay bills. We build conversion-focused webpages with strong CTAs, service showcases, and integrated tracking—turning visitors into booked calls, not wasted ad dollars."
    },
    {
      category: "Common Concerns",
      question: "We rank low on Google and our competitors always show up before us.",
      answer: "Without SEO, you're invisible online. We optimize your site structure, content, and local SEO so you appear when prospects search for your services. Over time, this reduces reliance on ads and builds a steady flow of free, organic leads."
    },
    {
      category: "Common Concerns",
      question: "We don't have time to manage ads and SEO—it's too technical.",
      answer: "That's where we come in. We set up, monitor, and optimize everything for you, sending clear reports so you can focus on running your business while we handle the digital growth."
    },
    {
      category: "Common Concerns",
      question: "Other agencies locked us into long contracts with no results. Will you?",
      answer: "No. We believe in earning your business month after month. Our systems are designed to produce measurable growth, so you'll stay with us because it works—not because you're trapped."
    },
    {
      category: "Common Concerns",
      question: "Our leads from ads are low quality or not in our service area.",
      answer: "We use geo-targeting and demographic filters to ensure your ads only appear to qualified prospects in the right areas. This means no more chasing 'tire kickers'—just real leads ready to book."
    },
    {
      category: "Common Concerns",
      question: "We already get word-of-mouth clients. Why add marketing?",
      answer: "Word-of-mouth is great, but it's unpredictable. With VGP systems, you'll build a reliable pipeline of leads that fuels consistent revenue and lets you choose the clients you want—not just the ones who stumble in."
    },

    // Service-Specific Questions
    {
      category: "Services",
      question: "What's the difference between your packages?",
      answer: "Growth Start focuses on foundational SEO and basic ads. Business Accelerator adds advanced funnels and automation. Enterprise packages include comprehensive strategies, priority support, and dedicated account management."
    },
    {
      category: "Services",
      question: "Do you create content for social media?",
      answer: "Yes, our packages include content creation for social media platforms relevant to your business. We create posts, images, and videos that align with your brand and engage your target audience."
    },
    {
      category: "Services",
      question: "Can you redesign our website?",
      answer: "Absolutely. We specialize in conversion-focused website design that turns visitors into leads. Our designs are mobile-optimized, fast-loading, and built with SEO in mind."
    },
    {
      category: "Services",
      question: "Do you handle Google My Business optimization?",
      answer: "Yes, local SEO including Google My Business optimization is a core component of our services. We optimize your listing, manage reviews, and ensure you appear in local search results."
    },
    {
      category: "Services",
      question: "What about email marketing?",
      answer: "Email marketing is included in our Business Accelerator and Enterprise packages. We create automated sequences, newsletters, and targeted campaigns to nurture leads and retain customers."
    },
    {
      category: "Services",
      question: "Do you provide copywriting services?",
      answer: "Yes, professional copywriting is included in all packages. We write ad copy, website content, email sequences, and landing pages that convert visitors into customers."
    },

    // Partnership & Working Relationship
    {
      category: "Partnership",
      question: "How involved do I need to be in the process?",
      answer: "We handle 90% of the work. You'll need to provide initial business insights, approve major strategies, and respond to urgent opportunities. Most clients spend 2-3 hours per month on marketing activities."
    },
    {
      category: "Partnership",
      question: "What if we want to bring marketing in-house later?",
      answer: "We're happy to train your team and provide transition support. Many of our long-term clients eventually develop internal capabilities while keeping us for strategy and advanced campaigns."
    },
    {
      category: "Partnership",
      question: "Can we start with a smaller package and upgrade later?",
      answer: "Absolutely. Many clients start with Growth Start and upgrade as they see results. We'll recommend the best starting point based on your goals and budget during our strategy call."
    },
    {
      category: "Partnership",
      question: "Do you work with our existing marketing team?",
      answer: "Yes, we often collaborate with internal marketing teams. We can provide strategy oversight, technical expertise, or handle specific channels while your team focuses on other areas."
    },
    {
      category: "Partnership",
      question: "What makes you different from other agencies?",
      answer: "We focus on measurable results, not vanity metrics. Our team combines technical expertise with creative strategy, and we provide transparent reporting. Plus, no long-term contracts means we earn your business every month."
    }
  ];

  const categories = [...new Set(faqData.map(item => item.category))];

  return (
    <>
      <EnhancedSEO 
        type="resources"
        title="Frequently Asked Questions | VGP Digital Marketing Agency"
        description="Get answers to common questions about our SEO, paid advertising, and digital marketing services. Learn about pricing, timelines, processes, and what to expect when working with VGP."
        keywords="FAQ, digital marketing questions, SEO questions, paid advertising help, marketing agency support, VGP questions"
        url="/faq"
      />
      
      <div className="min-h-screen bg-background py-20">
        <div className="container mx-auto px-4">
          {/* Header Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Get instant answers to the most common questions about our digital marketing services, 
              processes, and what you can expect when partnering with VGP.
            </p>
            
            {/* Quick Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button size="lg" className="bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90" asChild>
                <a href="/book-call">
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Free Strategy Call
                </a>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <a href="tel:07450620757">
                  <Phone className="w-5 h-5 mr-2" />
                  Call: 07450 620757
                </a>
              </Button>
            </div>
            
            {/* Additional Links */}
            <div className="flex flex-wrap gap-3 justify-center items-center mt-6">
              <Button variant="ghost" size="sm" asChild>
                <a href="/our-commitments" className="text-sm underline underline-offset-4 hover:text-primary">
                  Our Commitments
                </a>
              </Button>
              <Button variant="ghost" size="sm" asChild>
                <a href="/culture" className="text-sm underline underline-offset-4 hover:text-primary">
                  Our Culture
                </a>
              </Button>
            </div>
          </div>

          {/* FAQ Categories */}
          <div className="space-y-8">
            {categories.map((category) => (
              <div key={category} className="mb-12">
                <h2 className="text-2xl font-bold mb-6 text-primary border-b border-border pb-2">
                  {category}
                </h2>
                <div className="space-y-4">
                  {faqData
                    .filter(item => item.category === category)
                    .map((item, index) => {
                      const globalIndex = faqData.indexOf(item);
                      const isOpen = openItems.includes(globalIndex);
                      
                      return (
                        <Card key={globalIndex} className="border border-border hover:border-primary/20 transition-colors">
                          <CardContent className="p-0">
                            <button
                              onClick={() => toggleItem(globalIndex)}
                              className="w-full text-left p-6 flex justify-between items-center hover:bg-muted/50 transition-colors"
                            >
                              <h3 className="text-lg font-semibold text-foreground pr-4">
                                {item.question}
                              </h3>
                              {isOpen ? (
                                <ChevronUp className="w-5 h-5 text-primary flex-shrink-0" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                              )}
                            </button>
                            
                            {isOpen && (
                              <div className="px-6 pb-6">
                                <div className="border-t border-border pt-4">
                                  <p className="text-muted-foreground leading-relaxed">
                                    {item.answer}
                                  </p>
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                </div>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="mt-16 text-center bg-gradient-to-r from-primary/10 to-secondary/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-4 text-foreground">
              Still have questions?
            </h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Our team is here to help. Book a free strategy call to discuss your specific needs, 
              or give us a call to speak with a digital marketing expert right away.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90" asChild>
                <a href="/book-call">
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Free Consultation
                </a>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <a href="tel:07450620757">
                  <Phone className="w-5 h-5 mr-2" />
                  Speak to an Expert Now
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FAQ;